import React, { type FunctionComponent } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormSelect } from 'domains/merchandising/components/form-input/MerchandisingFormSelect'
import {
  bannerDestinationTypes,
  placementDestinationOptions,
  styleOptions,
} from 'domains/banners/constants/banner.constants'
import { ButtonVariantEnum } from 'domains/banners/api'
import { useSelectOptionsDashMessage } from 'domains/banners/utils/intl.hooks'
import { MerchandisingFormInput } from 'domains/merchandising/components/form-input/MerchandisingFormInput'
import { type FlyoutFormData } from 'domains/banners/types/banner.types'

interface Props<T> {
  form: FormContextValues<T>
  defaultDisplayValue: string
  destinationField: keyof T
  buttonField: keyof T
}

type FlyoutLinkProps = Props<FlyoutFormData>

export const FlyoutFormDestination: FunctionComponent<FlyoutLinkProps> = ({
  form,
  destinationField,
  buttonField,
  defaultDisplayValue, // why?
}) => {
  const { watch, register, setValue } = form

  const destination = (s: string) => destinationField + '.' + s
  const button = (s: string) => buttonField + '.' + s

  const defaultStyle = () => {
    switch (destinationField) {
      case 'primaryDestination':
        return ButtonVariantEnum.Primary
      case 'secondaryDestination':
        return ButtonVariantEnum.Secondary
      case 'linkDestination':
        return ButtonVariantEnum.Link
      default:
        return ButtonVariantEnum.Secondary
    }
  }

  const titleId = () => {
    switch (destinationField) {
      case 'primaryDestination':
        return 'bannersDomain.flyoutForm.form.primaryDestination.title'
      case 'secondaryDestination':
        return 'bannersDomain.flyoutForm.form.secondaryDestination.title'
      case 'linkDestination':
        return 'bannersDomain.flyoutForm.form.linkDestination.title'
      default:
        return 'bannersDomain.flyoutForm.form.primaryDestination.title'
    }
  }

  const buttonLabelValue: string = watch(button('label'))
  const buttonStyleValue: string = watch(button('styleVariant')) || defaultStyle()
  const destinationType: string = watch(destination('type')) || bannerDestinationTypes.doNotNavigate
  const destinationTarget: string = watch(destination('target')) || ''
  const openInNewTab: string = watch(destination('external.open_in_new_tab')) || 'false'
  const openInNewWindow: string = watch(destination('external.open_in_new_window')) || 'false'
  const isDoNotNavigate = destinationType === bannerDestinationTypes.doNotNavigate
  const isExternal = destinationType === bannerDestinationTypes.external
  const destinationOptions = useSelectOptionsDashMessage(placementDestinationOptions.slice(0, 2))
  const styleDisplayOptions = useSelectOptionsDashMessage(styleOptions)

  return (
    <MerchandisingFormSection
      titleId={titleId()}
      tooltipId="bannersDomain.flyoutForm.form.destination.tooltip"
      renderAction
    >
      <input type="hidden" value={buttonLabelValue} name={button('label')} ref={register} />
      {/* <input type="hidden" value={buttonStyleValue} name={button('styleVariant')} ref={register} /> */}
      <input type="hidden" value={destinationTarget} name={destination('target')} ref={register} />
      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingFormInput
            ref={register({ required: destinationField == 'primaryDestination' })}
            labelId="bannersDomain.flyoutForm.form.destination.cta"
            identifier={button('label')}
            defaultValue={''}
          />
        </MerchandisingFormField>
        <MerchandisingFormField>
          <MerchandisingFormSelect
            form={form}
            labelId="bannersDomain.bannersForm.form.link-destination.input.destinationType.style"
            identifier={button('styleVariant')}
            onChange={selectedOption => setValue(button('styleVariant'), selectedOption.value)}
            options={styleDisplayOptions}
            defaultValue={buttonStyleValue}
          />
        </MerchandisingFormField>
        <MerchandisingFormField>
          <MerchandisingFormSelect
            form={form}
            labelId="bannersDomain.bannersForm.form.link-destination.input.destinationType.label"
            identifier={destination('type')}
            onChange={() => setValue(destination('target'), null)}
            options={destinationOptions}
            defaultValue={destinationType}
          />
        </MerchandisingFormField>
      </MerchandisingFormRow>
      {isDoNotNavigate &&
        setValue(
          'destination.donotnavigate.record_click_attempt',
          '{"record_click_attempt":false}'
        )}
      {isExternal && (
        <div>
          <MerchandisingFormRow>
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: true })}
                labelId="bannersDomain.bannersForm.form.link-destination.input.destination-target.external.url"
                identifier={destination('external.url')}
                defaultValue={''}
              />
            </MerchandisingFormField>
          </MerchandisingFormRow>
          <MerchandisingFormRow>
            <MerchandisingFormField>
              <MerchandisingFormSelect
                form={form}
                labelId="bannersDomain.bannersForm.form.link-destination.input.destination-target.external.open-in-new-tab"
                identifier={destination('external.open_in_new_tab')}
                options={[
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' },
                ]}
                onChange={({ value }) => setValue(destination('external.open_in_new_tab'), value)}
                defaultValue={openInNewTab}
              />
            </MerchandisingFormField>
            <MerchandisingFormField>
              <MerchandisingFormSelect
                form={form}
                labelId="bannersDomain.bannersForm.form.link-destination.input.destination-target.external.open-in-new-window"
                identifier={destination('external.open_in_new_window')}
                options={[
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' },
                ]}
                onChange={({ value }) =>
                  setValue(destination('external.open_in_new_window'), value)
                }
                defaultValue={openInNewWindow}
              />
            </MerchandisingFormField>
          </MerchandisingFormRow>
        </div>
      )}
    </MerchandisingFormSection>
  )
}
